"use client"

import { useCloseOnScroll } from "@repo/shared"
import { useCallback, useEffect, useState } from "react"
import { Select, SelectProps } from "antd"

import SelectPicker from "../SelectPicker"

type SelectOption<T = number | string> = { label: string; value: T }

type IProps<T = number | string> = {
	value?: T
	options: SelectOption<T>[]
	onChange: (value: T) => void
	placeholder?: string
	className?: string
	labelRender?: SelectProps["labelRender"]
	getPopupContainer?: SelectProps["getPopupContainer"]
}

/**
 * 通用的Select组件，根据窗口宽度自动切换PC版和H5版
 * - PC（宽度 >= 768px）：使用 antd Select
 * - H5（宽度 < 768px）：使用 SelectPickerV2
 */
const SelectMix = <T extends number | string = number | string>({
	value,
	options,
	onChange,
	placeholder = "Please select",
	className,
	labelRender,
	getPopupContainer,
}: IProps<T>) => {
	const [isMobile, setIsMobile] = useState(false)
	const [visible, setVisible] = useState(false)
	const [selectOpen, setSelectOpen] = useState<boolean | undefined>(undefined)

	useEffect(() => {
		const checkIsMobile = () => {
			if (typeof window !== "undefined") {
				setIsMobile(window.innerWidth < 768)
			}
		}

		checkIsMobile()
		window.addEventListener("resize", checkIsMobile)
		return () => {
			window.removeEventListener("resize", checkIsMobile)
		}
	}, [])

	// 关闭下拉框的回调
	const handleCloseSelect = useCallback(() => {
		setSelectOpen(false)
	}, [])

	const shouldIgnoreSelectDropdownScroll = useCallback((event: Event) => {
		const path = event.composedPath()
		const dropdowns = document.querySelectorAll(".ant-select-dropdown")

		return (
			Array.from(dropdowns).some((dropdown) => dropdown.matches(":hover")) ||
			path.some((target) => target instanceof Element && Boolean(target.closest(".ant-select-dropdown")))
		)
	}, [])

	// 页面滚动时自动关闭下拉框（仅桌面端）
	useCloseOnScroll(selectOpen ?? false, handleCloseSelect, !isMobile, {
		shouldIgnoreScroll: shouldIgnoreSelectDropdownScroll,
	})

	// 处理移动端选择确认
	const handleMobileConfirm = useCallback(
		(selectedValues: T[]) => {
			if (selectedValues.length > 0) {
				onChange(selectedValues[0])
			}
			setVisible(false)
		},
		[onChange]
	)

	// 处理PC端选择变化
	const handlePcChange = useCallback(
		(selectedValue: T) => {
			onChange(selectedValue)
		},
		[onChange]
	)

	// 处理移动端点击，显示选择器
	const handleMobileFocus = useCallback(() => {
		if (isMobile) {
			setVisible(true)
		}
	}, [isMobile])

	// 处理 Select 点击
	const handleSelectClick = useCallback(() => {
		if (!isMobile) {
			setSelectOpen((prev) => !prev)
		}
	}, [isMobile])

	// 关闭移动端选择器
	const handleClosePicker = useCallback(() => {
		setVisible(false)
	}, [])

	return (
		<>
			<Select
				open={isMobile ? false : selectOpen}
				listHeight={400}
				className={className}
				placeholder={placeholder}
				options={options}
				value={value}
				onChange={handlePcChange}
				onClick={handleSelectClick}
				labelRender={labelRender}
				getPopupContainer={getPopupContainer || ((triggerNode) => triggerNode.parentNode)}
				onFocus={handleMobileFocus}
			/>

			{/* H5选择器 */}
			<SelectPicker<T>
				visible={visible}
				columns={[options]}
				defaultValue={value !== undefined ? [value] : undefined}
				onClose={handleClosePicker}
				onConfirm={handleMobileConfirm}
			/>
		</>
	)
}

export default SelectMix
